import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Download as DownloadIcon } from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  Grid,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Approval as ApprovalIcon,
  ThumbUp as ThumbUpIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  TimerOff as TimerOffIcon,
  Drafts as DraftsIcon,
  Calculate as CalculateIcon,
  Percent as PercentIcon,
} from "@mui/icons-material";
import {
  fetchSalaryConfigurations,
  deleteExistingSalaryConfiguration,
  approveExistingSalaryConfiguration,
} from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationActions";
import {
  selectSalaryConfigurations,
  selectSalaryConfigurationLoading,
  selectSalaryConfigurationApproving,
  selectSalaryConfigurationPagination,
} from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationSelectors";
import AddSalaryConfigurationModal from "./AddSalaryConfigurationModal";
import EditSalaryConfigurationModal from "./EditSalaryConfigurationModal";
import salaryConfigSvg from "assets/folders/salary-config.svg";

import usePermissions from "hooks/usePermissions";

const SalaryConfiguration = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const salaryConfigurations = useSelector(selectSalaryConfigurations);
  const loading = useSelector(selectSalaryConfigurationLoading);
  const approving = useSelector(selectSalaryConfigurationApproving);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [configToApprove, setConfigToApprove] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedConfigForView, setSelectedConfigForView] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [payrollMonth, setPayrollMonth] = useState(new Date().getMonth() + 1);
  const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const pagination = useSelector(selectSalaryConfigurationPagination);

  const { hasPermission, hasAnyPermission } = usePermissions();

  useEffect(() => {
    const params = {
      page,
      limit,
      payroll_month: payrollMonth,
      payroll_year: payrollYear,
    };
    if (selectedEmployeeId) {
      params.employee_id = selectedEmployeeId;
    }
    dispatch(fetchSalaryConfigurations(params));
  }, [dispatch, page, limit, payrollMonth, payrollYear, selectedEmployeeId]);

  const filteredConfigurations = salaryConfigurations.filter((config) => {
    const matchesSearch =
      searchTerm === "" ||
      `${config.first_name} ${config.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      config.employee_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || config.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      if (dateString.includes("T")) {
        const [datePart] = dateString.split("T");
        const [year, month, day] = datePart.split("-");
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
      }
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split("-");
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
      }
      return dateString;
    } catch (e) {
      return "-";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "-";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  const getStatusColor = (status) => {
    const colors = {
      active: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
        icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
      },
      draft: {
        bg: "#fef3c7",
        text: "#92400e",
        darkBg: "rgba(245,158,11,0.25)",
        darkText: "#fde68a",
        icon: <DraftsIcon sx={{ fontSize: 14 }} />,
      },
      inactive: {
        bg: "#fee2e2",
        text: "#991b1b",
        darkBg: "rgba(239,68,68,0.25)",
        darkText: "#fca5a5",
        icon: <CancelIcon sx={{ fontSize: 14 }} />,
      },
      expired: {
        bg: "#fafafa",
        text: "#575640",
        darkBg: "rgba(15, 15, 15, 0.25)",
        darkText: "#c0c0c0",
        icon: <TimerOffIcon sx={{ fontSize: 14 }} />,
      },
    };
    return colors[status] || colors.draft;
  };

  const StatusBadge = ({ status }) => {
    const colors = getStatusColor(status);
    const labels = {
      active: "Active",
      draft: "Draft",
      inactive: "Inactive",
      expired: "Expired",
    };
    return (
      <Chip
        icon={colors.icon}
        label={labels[status] || status}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: colors.darkBg,
            color: colors.darkText,
          }),
        }}
      />
    );
  };

  const handleDeleteClick = (config) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (configToDelete) {
      setDeleting(true);
      const result = await dispatch(
        deleteExistingSalaryConfiguration(configToDelete.id),
      );
      if (result.success) {
        dispatch(fetchSalaryConfigurations());
        setDeleteDialogOpen(false);
        setConfigToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  const handleApproveClick = (config) => {
    setConfigToApprove(config);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (configToApprove) {
      const result = await dispatch(
        approveExistingSalaryConfiguration(configToApprove.id),
      );
      if (result.success) {
        dispatch(fetchSalaryConfigurations());
        setApproveDialogOpen(false);
        setConfigToApprove(null);
      }
    }
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setConfigToApprove(null);
  };

  const handleViewClick = (config) => {
    setSelectedConfigForView(config);
    setViewDialogOpen(true);
  };

  if (!hasPermission("salary_configuration.view")) {
    return null;
  }
  const exportSalaryConfigurationsToExcel = () => {
    if (!filteredConfigurations || filteredConfigurations.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Yellow header style
    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 12,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFFF00" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["SALARY CONFIGURATIONS REPORT"]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 10 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // Summary
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Configurations", filteredConfigurations.length],
        [
          "Active",
          filteredConfigurations.filter((c) => c.status === "active").length,
        ],
        [
          "Draft",
          filteredConfigurations.filter((c) => c.status === "draft").length,
        ],
        [
          "Inactive",
          filteredConfigurations.filter((c) => c.status === "inactive").length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 6;

    // Headers
    const headers = [
      "ID",
      "Employee ID",
      "Employee Code",
      "Employee Name",
      "Email",
      "Phone",
      "Basic Salary",
      "Gross Salary",
      "Total Deduction",
      "Net Salary",
      "Status",
      "Note",
      "Date",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    // Apply yellow header style to header row
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // Data
    filteredConfigurations.forEach((config) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            config.id || "",
            config.employee_id || "",
            config.employee_code || "",
            `${config.first_name || ""} ${config.last_name || ""}`.trim(),
            config.email || "",
            config.phone || "",
            config.basic ? formatCurrency(config.basic) : "",
            config.gross_salary ? formatCurrency(config.gross_salary) : "",
            config.total_deduction
              ? formatCurrency(config.total_deduction)
              : "",
            config.net_salary ? formatCurrency(config.net_salary) : "",
            config.status || "",
            config.note || "",
            config.created_at ? formatDateTime(config.created_at) : "",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    // Column widths
    ws["!cols"] = headers.map((_, index) => {
      const widths = {
        0: 15, // ID
        1: 18, // Employee ID
        2: 20, // Employee Code
        3: 28, // Employee Name
        4: 30, // Email
        5: 18, // Phone
        6: 20, // Basic Salary
        7: 20, // Gross Salary
        8: 22, // Total Deduction
        9: 20, // Net Salary
        10: 18, // Status
        11: 30, //note
        11: 25, // Created At
      };
      return { wch: widths[index] || 20 };
    });

    XLSX.utils.book_append_sheet(wb, ws, "Salary Configurations");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Salary_Configurations_Report_${
        new Date().toISOString().split("T")[0]
      }.xlsx`,
    );
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={salaryConfigSvg}
              alt="Salary Configurations"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Employee Salary Configurations
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage individual employee salary structures
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Export Excel Button */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportSalaryConfigurationsToExcel}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Excel
            </Button>

            {hasPermission("salary_configuration.create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Assign Salary Configuration
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by employee name, code or email..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            // If search term is empty, clear employee filter
            if (e.target.value === "") {
              setSelectedEmployeeId(null);
            }
          }}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 300 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Month Filter */}
        <TextField
          select
          size="small"
          label="Month"
          value={payrollMonth}
          onChange={(e) => setPayrollMonth(Number(e.target.value))}
          sx={{
            minWidth: 140,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
            },
          }}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
            <MenuItem key={month} value={month}>
              {new Date(2026, month - 1).toLocaleString("default", {
                month: "long",
              })}
            </MenuItem>
          ))}
        </TextField>

        {/* Year Filter */}
        <TextField
          select
          size="small"
          label="Year"
          value={payrollYear}
          onChange={(e) => setPayrollYear(Number(e.target.value))}
          sx={{
            minWidth: 120,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
            },
          }}
        >
          {Array.from(
            { length: 5 },
            (_, i) => new Date().getFullYear() - 2 + i,
          ).map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </TextField>

        <Stack direction="row" spacing={1}>
          {["all", "active", "inactive"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "primary.main" }} />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading salary configurations...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    ID / Structure
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Employee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Attendance Confirmation
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Basic Salary
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Gross Salary
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Total Deduction
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Net Salary
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      minWidth: "90px",
                      textAlign: "center",
                    }}
                  >
                    Note
                  </TableCell>
                  {hasAnyPermission([
                    "salary_configuration.update",
                    "salary_configuration.delete",
                    "salary_configuration.approve",
                  ]) && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConfigurations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <MoneyIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No salary configurations found. Click "Assign Salary
                        Configuration" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredConfigurations.map((config, index) => (
                    <TableRow
                      key={config.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "primary.main",
                              fontFamily: "monospace",
                            }}
                          >
                            #{config.id}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#98a3b8"
                                  : "#4c5462",
                            }}
                          >
                            Structure: {config.salary_structure_id}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#98a3b8"
                                  : "#4c5462",
                            }}
                          >
                            Tenant: {config.tenant_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              background:
                                theme.palette.mode === "dark"
                                  ? "linear-gradient(135deg, rgba(245,158,11,0.2) 0%, rgba(217,119,6,0.2) 100%)"
                                  : "linear-gradient(135deg, #fef3c7 0%, #fef08a 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <PersonIcon sx={{ color: "primary.main" }} />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                color: "text.primary",
                              }}
                            >
                              {config.first_name} {config.last_name}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 400,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#f59e0b"
                                    : "#b8790c",
                                fontWeight: 500,
                                fontFamily: "monospace",
                              }}
                            >
                              {config.employee_code}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                fontStyle: "italic",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#8793ab"
                                    : "#777c84",
                              }}
                            >
                              {config.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            config.attendance_confirmation_status || "pending"
                          }
                          size="small"
                          sx={{
                            textTransform: "capitalize",
                            backgroundColor:
                              config.attendance_confirmation_status ===
                              "confirmed"
                                ? "#d1fae5"
                                : config.attendance_confirmation_status ===
                                  "pending"
                                ? "#fef3c7"
                                : "#fee2e2",
                            color:
                              config.attendance_confirmation_status ===
                              "confirmed"
                                ? "#065f46"
                                : config.attendance_confirmation_status ===
                                  "pending"
                                ? "#92400e"
                                : "#991b1b",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          align="center"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "primary.main",
                          }}
                        >
                          {formatCurrency(config.basic)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          align="center"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {formatCurrency(config.gross_salary)}
                        </Typography>
                      </TableCell>
                      {/* Total Deduction Column */}
                      <TableCell>
                        <Typography
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            color: "#bf2626e4",
                          }}
                        >
                          {formatCurrency(config.total_deduction)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          align="center"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 700,
                            color: "#10b981",
                          }}
                        >
                          {formatCurrency(config.net_salary)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={config.status} />
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontStyle: "italic",
                            color: "text.secondary",
                            textAlign: "center",
                          }}
                        >
                          {config.note || "-"}
                        </Typography>
                      </TableCell>
                      {hasAnyPermission([
                        "salary_configuration.update",
                        "salary_configuration.delete",
                        "salary_configuration.approve",
                      ]) && (
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Tooltip title="View Details">
                              <IconButton
                                onClick={() => handleViewClick(config)}
                                size="small"
                                sx={{
                                  color: "#3b82f6",
                                  "&:hover": { backgroundColor: "#dbeafe" },
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {hasPermission("salary_configuration.update") && (
                              <Tooltip title="Edit">
                                <IconButton
                                  onClick={() => {
                                    setSelectedConfig(config);
                                    setShowEditModal(true);
                                  }}
                                  size="small"
                                  sx={{
                                    color: "primary.main",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.mode === "dark"
                                          ? "rgba(245,158,11,0.2)"
                                          : "#fffbeb",
                                    },
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasPermission("salary_configuration.delete") && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteClick(config)}
                                  size="small"
                                  sx={{
                                    color: "#ef4444",
                                    "&:hover": { backgroundColor: "#fee2e2" },
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {config.status === "draft" &&
                              hasPermission("salary_configuration.approve") && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleApproveClick(config)}
                                  disabled={approving}
                                  startIcon={
                                    <ThumbUpIcon sx={{ fontSize: 16 }} />
                                  }
                                  sx={{
                                    backgroundColor: "#10b981",
                                    textTransform: "none",
                                    fontSize: "0.7rem",
                                    fontWeight: 500,
                                    borderRadius: "8px",
                                    padding: "4px 12px",
                                    minWidth: "auto",
                                    "&:hover": {
                                      backgroundColor: "#059669",
                                    },
                                  }}
                                >
                                  Approve
                                </Button>
                              )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Count Summary */}
      {!loading && salaryConfigurations.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">
            Total Configurations: {salaryConfigurations.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredConfigurations.length} of{" "}
            {salaryConfigurations.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading &&
        filteredConfigurations.length === 0 &&
        salaryConfigurations.length > 0 && (
          <Paper
            sx={{
              mt: 4,
              p: 4,
              textAlign: "center",
              borderRadius: "16px",
              bgcolor: "background.paper",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
            <Typography
              sx={{
                color: "text.secondary",
                mb: 2,
              }}
            >
              No salary configurations match your search criteria
            </Typography>
            <Button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
              sx={{ color: "primary.main", textTransform: "none" }}
            >
              Clear filters
            </Button>
          </Paper>
        )}

      {/* Modals */}
      <AddSalaryConfigurationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          dispatch(
            fetchSalaryConfigurations({
              page,
              limit,
              payroll_month: payrollMonth,
              payroll_year: payrollYear,
            }),
          );
        }}
      />

      <EditSalaryConfigurationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedConfig(null);
        }}
        salaryConfig={selectedConfig}
        onSuccess={() => {
          dispatch(
            fetchSalaryConfigurations({
              page,
              limit,
              payroll_month: payrollMonth,
              payroll_year: payrollYear,
            }),
          );
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Delete Salary Configuration
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to delete the salary configuration for
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "primary.main",
              textAlign: "center",
              mb: 2,
            }}
          >
            "{configToDelete?.first_name} {configToDelete?.last_name}"
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleApproveCancel}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleApproveCancel}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#10b981", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Approve Salary Configuration
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to approve the salary configuration for
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "#10b981",
              textAlign: "center",
              mb: 2,
            }}
          >
            "{configToApprove?.first_name} {configToApprove?.last_name}"
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "0.875rem",
              color: "#6b7280",
              mb: 2,
            }}
          >
            Once approved, this salary configuration will become active for
            payroll processing.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleApproveCancel}
              disabled={approving}
              fullWidth
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveConfirm}
              disabled={approving}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              }}
            >
              {approving ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Approve"
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>
      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
        }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            maxWidth: "800px",
            margin: "16px",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                : "#ffffff",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        {selectedConfigForView && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {/* Header with Gradient */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                p: 3,
                pr: 5,
                position: "relative",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="img"
                  src={salaryConfigSvg}
                  alt="Details"
                  sx={{
                    width: 50,
                    height: 50,
                    objectFit: "contain",
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                  }}
                />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "#ffffff",
                      letterSpacing: "0.5px",
                    }}
                  >
                    SALARY CONFIGURATION DETAILS
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontSize: "0.85rem",
                    }}
                  >
                    {selectedConfigForView.first_name}{" "}
                    {selectedConfigForView.last_name} (
                    {selectedConfigForView.employee_code})
                  </Typography>
                </Box>
              </Stack>

              <IconButton
                onClick={() => setViewDialogOpen(false)}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  color: "#ffffff",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                maxHeight: "calc(80vh - 180px)",
                overflowY: "auto",
                overflowX: "hidden",
                p: 3,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#f59e0b",
                  borderRadius: "10px",
                },
              }}
            >
              <Stack spacing={3}>
                {/* Section 1: EMPLOYEE INFORMATION */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    EMPLOYEE INFORMATION
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    {/* Row 1 */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Config ID
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color: "primary.main",
                        }}
                      >
                        #{selectedConfigForView.id}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Employee Code
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                        }}
                      >
                        {selectedConfigForView.employee_code}
                      </Typography>
                    </Box>
                    {/* Row 2 */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Structure ID
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color: "primary.main",
                        }}
                      >
                        #{selectedConfigForView.salary_structure_id}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Status
                      </Typography>
                      <StatusBadge status={selectedConfigForView.status} />
                    </Box>
                    {/* Row 3 */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {selectedConfigForView.email}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Phone
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {selectedConfigForView.phone || "-"}
                      </Typography>
                    </Box>
                    {/* Row 4 - Full width */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {formatDate(selectedConfigForView.effective_from)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* Section 2: SALARY BREAKDOWN */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    SALARY BREAKDOWN
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "8px 16px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Basic Salary
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          color: "primary.main",
                        }}
                      >
                        {formatCurrency(selectedConfigForView.basic)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        HR Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(selectedConfigForView.hr_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Special Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(
                          selectedConfigForView.special_allowance,
                        )}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        DA
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(selectedConfigForView.da)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Conveyance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(
                          selectedConfigForView.conveyance_allowance,
                        )}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Mobile Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(selectedConfigForView.mobile_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Other Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(selectedConfigForView.other_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Incentive
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(selectedConfigForView.incentive)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Travel Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(selectedConfigForView.travel_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Bonus
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(selectedConfigForView.bonus)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Mediclaim
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(selectedConfigForView.mediclaim)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Section 3: SALARY SUMMARY CARDS */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    SALARY SUMMARY
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textAlign: "center",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(59,130,246,0.15)"
                              : "#eff6ff",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(59,130,246,0.3)"
                              : "#bfdbfe"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#3b82f6",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Gross Salary
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: "#3b82f6",
                          }}
                        >
                          {formatCurrency(selectedConfigForView.gross_salary)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textAlign: "center",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(139,92,246,0.15)"
                              : "#ede9fe",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(139,92,246,0.3)"
                              : "#c4b5fd"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#8b5cf6",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          CTC Component
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: "#8b5cf6",
                          }}
                        >
                          {formatCurrency(
                            selectedConfigForView.total_ctc_component,
                          )}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textAlign: "center",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(239,68,68,0.15)"
                              : "#fee2e2",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(239,68,68,0.3)"
                              : "#fca5a5"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#ef4444",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Total Deduction
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: "#ef4444",
                          }}
                        >
                          {formatCurrency(
                            selectedConfigForView.total_deduction,
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textAlign: "center",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(16,185,129,0.15)"
                              : "#d1fae5",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(16,185,129,0.3)"
                              : "#6ee7b7"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#10b981",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Net Salary
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: "#10b981",
                          }}
                        >
                          {formatCurrency(selectedConfigForView.net_salary)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Section 4: DEDUCTION SETTINGS */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    DEDUCTION SETTINGS
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "8px 16px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        TDS %
                      </Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                        {selectedConfigForView.tds_percentage}%{" "}
                        {selectedConfigForView.tds_type === "number"
                          ? "(Derived)"
                          : ""}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        TDS Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: "#ef4444",
                        }}
                      >
                        {formatCurrency(selectedConfigForView.tds)}{" "}
                        {selectedConfigForView.tds_type === "percentage"
                          ? "(Derived)"
                          : ""}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        PF
                      </Typography>
                      <Chip
                        label={
                          selectedConfigForView.pf_applicable === 1
                            ? `YES (${
                                selectedConfigForView.pf_type === "number"
                                  ? "₹" + selectedConfigForView.pf_amount
                                  : selectedConfigForView.pf_percentage + "%"
                              })`
                            : "NO"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            selectedConfigForView.pf_applicable === 1
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            selectedConfigForView.pf_applicable === 1
                              ? "#065f46"
                              : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "24px",
                          borderRadius: "6px",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        ESI
                      </Typography>
                      <Chip
                        label={
                          selectedConfigForView.esi_applicable === 1
                            ? `YES (${
                                selectedConfigForView.esi_type === "number"
                                  ? "₹" + selectedConfigForView.esi_amount
                                  : selectedConfigForView.esi_percentage + "%"
                              })`
                            : "NO"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            selectedConfigForView.esi_applicable === 1
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            selectedConfigForView.esi_applicable === 1
                              ? "#065f46"
                              : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "24px",
                          borderRadius: "6px",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        PT
                      </Typography>
                      <Chip
                        label={
                          selectedConfigForView.pt_applicable === 1
                            ? `YES (${
                                selectedConfigForView.pt_type === "number"
                                  ? "₹" + selectedConfigForView.pt_amount
                                  : selectedConfigForView.pt_percentage + "%"
                              })`
                            : "NO"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            selectedConfigForView.pt_applicable === 1
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            selectedConfigForView.pt_applicable === 1
                              ? "#065f46"
                              : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "24px",
                          borderRadius: "6px",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Attendance Based Salary
                      </Typography>
                      <Chip
                        label={
                          selectedConfigForView.attendance_based_salary === 1
                            ? "YES"
                            : "NO"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            selectedConfigForView.attendance_based_salary === 1
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            selectedConfigForView.attendance_based_salary === 1
                              ? "#065f46"
                              : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "24px",
                          borderRadius: "6px",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                {/* Section 4.5: ADDITIONAL DEDUCTION */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    ADDITIONAL DEDUCTION
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Deduction Name
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 500,
                          color: selectedConfigForView.deduction_name
                            ? "text.primary"
                            : "#9ca3af",
                        }}
                      >
                        {selectedConfigForView.deduction_name || "-"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Deduction Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: "#bf2626e4",
                        }}
                      >
                        {formatCurrency(selectedConfigForView.deduction_amount)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                        gridColumn: "span 2",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Note
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontStyle: "italic",
                          color: selectedConfigForView.note
                            ? "text.secondary"
                            : "#9ca3af",
                        }}
                      >
                        {selectedConfigForView.note || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {/* Section 5: TIMESTAMPS */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    TIMESTAMPS
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Created At
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatDateTime(selectedConfigForView.created_at)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Updated At
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatDateTime(selectedConfigForView.updated_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2.5,
                borderTop: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={() => {
                  setSelectedConfig(selectedConfigForView);
                  setViewDialogOpen(false);
                  setShowEditModal(true);
                }}
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{
                  borderRadius: "10px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderColor: "#f59e0b",
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "#d97706",
                    backgroundColor: "rgba(245,158,11,0.08)",
                  },
                }}
              >
                Edit Configuration
              </Button>
              <Button
                onClick={() => setViewDialogOpen(false)}
                variant="contained"
                sx={{
                  borderRadius: "10px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </motion.div>
        )}
      </Dialog>
    </Box>
  );
};

export default SalaryConfiguration;
